import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role = 'technician') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  // Role checks
  const isAdmin = () => user?.role === 'admin';
  const isSupplyChainManager = () => user?.role === 'supply_chain_manager';
  const isManager = () => user?.role === 'manager';
  const isTechnician = () => user?.role === 'technician';
  
  // Permission checks - Granular RBAC
  const canViewPrices = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  const canManageUsers = () => user?.role === 'admin';
  const canManageSettings = () => user?.role === 'admin';
  
  // Parts CRUD - Admin, SCM, Manager can manage
  const canManageParts = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  
  // Categories/Locations - Admin, Manager only (SCM cannot)
  const canManageCategories = () => ['admin', 'manager'].includes(user?.role);
  const canManageLocations = () => ['admin', 'manager'].includes(user?.role);
  
  // Suppliers - Admin, SCM, Manager can manage
  const canManageSuppliers = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  
  // Tools - Admin, SCM, Manager can manage
  const canManageTools = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  
  // Customers - Admin, SCM, Manager can create/edit; Only Admin can delete
  const canManageCustomers = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  const canDeleteCustomers = () => user?.role === 'admin';
  
  // Stock Issuance - Admin, SCM, Manager can create
  const canCreateIssuance = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  
  // Transfers - Admin, SCM, Manager can create
  const canCreateTransfers = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  
  // Purchase Orders - Admin, SCM, Manager can create; Admin, SCM can approve/delete
  const canCreatePO = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  const canApprovePO = () => ['admin', 'supply_chain_manager'].includes(user?.role);
  const canDeletePO = () => ['admin', 'supply_chain_manager'].includes(user?.role);
  
  // Alerts - Admin, SCM, Manager can manage
  const canManageAlerts = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  
  // Legacy helpers for backward compatibility
  const canEdit = () => ['admin', 'supply_chain_manager', 'manager'].includes(user?.role);
  const canApprove = () => ['admin', 'supply_chain_manager'].includes(user?.role);
  const canDelete = () => ['admin', 'supply_chain_manager'].includes(user?.role);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      isAdmin,
      isSupplyChainManager,
      isManager,
      isTechnician,
      canEdit,
      canApprove,
      canManageUsers,
      canDelete,
      canViewPrices,
      canManageSettings,
      canManageParts,
      canManageCategories,
      canManageLocations,
      canManageSuppliers,
      canManageTools,
      canManageCustomers,
      canDeleteCustomers,
      canCreateIssuance,
      canCreateTransfers,
      canCreatePO,
      canApprovePO,
      canDeletePO,
      canManageAlerts
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
